/**
 * Reports — 6 standard society reports with PDF / Excel / CSV export.
 *
 * Layout:
 *   - PageHeader
 *   - TabsPills row to switch between report kinds
 *   - Active tab renders a small filter form + Preview + Download buttons +
 *     an inline result table/chart-ish summary so the admin can sanity-check
 *     the data before downloading.
 *
 * Downloads use a Bearer-authed `fetch` → Blob → anchor click flow (same
 * pattern as the audit-log CSV download). PDF + Excel + CSV all share that
 * helper.
 *
 * The page is admin/super_admin/accountant/treasurer/president visible —
 * router gate matches the server-side `REPORT_ROLES`.
 */
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  FileBarChart,
  Download,
  PlayCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { get, getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TabsPills } from '@/components/ui/tabs-pills';
import { EmptyState } from '@/components/ui/empty-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

type ReportKind =
  | 'financial-summary'
  | 'defaulter-aging'
  | 'visitor-trend'
  | 'helper-attendance'
  | 'election-participation'
  | 'audit-log';

const REPORT_OPTIONS: { value: ReportKind; label: string }[] = [
  { value: 'financial-summary', label: 'Financial' },
  { value: 'defaulter-aging', label: 'Defaulter aging' },
  { value: 'visitor-trend', label: 'Visitor trend' },
  { value: 'helper-attendance', label: 'Helper attendance' },
  { value: 'election-participation', label: 'Election' },
  { value: 'audit-log', label: 'Audit log' }
];

// ─── Download helper ────────────────────────────────────────────────────────

async function downloadReport(path: string, params: Record<string, string>): Promise<void> {
  const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:4100';
  const apiPrefix = import.meta.env.VITE_API_PREFIX ?? '/v1';
  const qs = new URLSearchParams(params).toString();
  const url = `${apiUrl}${apiPrefix}${path}${qs ? `?${qs}` : ''}`;

  const token = useAuthStore.getState().accessToken;
  const res = await fetch(url, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include'
  });

  if (!res.ok) {
    let msg = `Export failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: { code?: string; message?: string } };
      if (body?.error?.message) msg = body.error.message;
    } catch {
      /* non-JSON body */
    }
    throw new Error(msg);
  }

  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="([^"]+)"/.exec(cd);
  const fallback = `hooddesk-${path.replace(/[^a-z]/gi, '-')}-${new Date().toISOString().slice(0, 10)}`;
  const filename = m?.[1] ?? fallback;
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

function INR(n: number | null | undefined): string {
  if (n == null) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

// Default date pickers — last 30 / last 12-month window.
function today(): string {
  return new Date().toISOString().slice(0, 10);
}
function daysAgo(n: number): string {
  return new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);
}
function isoStart(d: string): string {
  return new Date(`${d}T00:00:00Z`).toISOString();
}
function isoEnd(d: string): string {
  return new Date(`${d}T23:59:59Z`).toISOString();
}

// ─── Inline preview shapes ──────────────────────────────────────────────────

interface FinancialPreview {
  window: { from: string; to: string };
  totals: {
    billed_inr: number;
    collected_inr: number;
    outstanding_inr: number;
    gst_inr: number;
    late_fees_inr: number;
  };
  months: Array<{
    month: string;
    billed: number;
    collected: number;
    outstanding: number;
    gst: number;
    late_fees: number;
  }>;
  line_items_breakdown: Array<{ label: string; amount: number }>;
}

interface DefaulterPreview {
  as_of: string;
  totals_by_bucket: {
    current_inr: number;
    d0_30_inr: number;
    d31_60_inr: number;
    d61_90_inr: number;
    d90_plus_inr: number;
    grand_total_inr: number;
  };
  rows: Array<{
    unit_label: string;
    current_inr: number;
    d0_30_inr: number;
    d31_60_inr: number;
    d61_90_inr: number;
    d90_plus_inr: number;
    total_inr: number;
  }>;
}

interface VisitorPreview {
  window: { from: string; to: string };
  totals: { approved: number; denied: number; fv_checkins: number; vehicle_entries: number };
  days: Array<{ day: string; approved: number; denied: number; no_show: number; fv_checkins: number; vehicle_entries: number }>;
  top_units: Array<{ label: string; count: number }>;
  top_visitors: Array<{ name: string; phone: string; count: number }>;
  peak_hours: Array<{ hour: number; count: number }>;
}

interface HelperPreview {
  window: { from: string; to: string };
  per_fv: Array<{ name: string; kind: string; phone: string; attendance_days: number; avg_minutes: number | null; no_show_count: number }>;
  per_unit: Array<{ unit_label: string; total_checkins: number }>;
  police_verification_expiring: Array<{ name: string; kind: string; police_verified_at: string; expires_in_days: number }>;
}

interface ElectionPreview {
  election: {
    id: string;
    title: string;
    role: string;
    status: string;
    secret_ballot: boolean;
    voter_eligibility: string;
  };
  turnout: { eligible: number; voted: number; pct: number };
  by_tower: Array<{ tower: string; count: number }>;
  candidates: Array<{ name: string; unit: string; vote_count: number | null; position: number | null; declared_winner: boolean; withdrawn: boolean }>;
}

interface AuditPreview {
  items: Array<{
    id: string;
    at: string;
    action: string;
    actor_role: string;
    entity_type: string;
    entity_id: string | null;
  }>;
  capped_at: number;
}

interface ElectionListItem {
  id: string;
  title: string;
  status: string;
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function ReportsPage(): JSX.Element {
  const [tab, setTab] = useState<ReportKind>('financial-summary');

  return (
    <>
      <PageHeader
        title="Reports"
        description="Generate AGM-ready financial, attendance, and audit reports — PDF, Excel, CSV."
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        <TabsPills
          value={tab}
          onChange={(v) => setTab(v as ReportKind)}
          options={REPORT_OPTIONS}
          aria-label="Report kinds"
        />
        {tab === 'financial-summary' && <FinancialTab />}
        {tab === 'defaulter-aging' && <DefaulterTab />}
        {tab === 'visitor-trend' && <VisitorTab />}
        {tab === 'helper-attendance' && <HelperTab />}
        {tab === 'election-participation' && <ElectionTab />}
        {tab === 'audit-log' && <AuditTab />}
      </div>
    </>
  );
}

// ─── Financial tab ──────────────────────────────────────────────────────────

function FinancialTab(): JSX.Element {
  const [from, setFrom] = useState<string>(daysAgo(365));
  const [to, setTo] = useState<string>(today());
  const [preview, setPreview] = useState<FinancialPreview | null>(null);
  const [busy, setBusy] = useState<null | 'preview' | 'pdf'>(null);

  const params = useMemo(() => ({ from: isoStart(from), to: isoEnd(to) }), [from, to]);

  const onPreview = async (): Promise<void> => {
    try {
      setBusy('preview');
      const data = await get<FinancialPreview>('/reports/financial-summary', {
        params: { ...params, format: 'json' }
      });
      setPreview(data);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const onPdf = async (): Promise<void> => {
    try {
      setBusy('pdf');
      await downloadReport('/reports/financial-summary', { ...params, format: 'pdf' });
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-vt-4 p-vt-4">
        <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-4">
          <Field label="From">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <ActionRow>
            <Button onClick={onPreview} disabled={busy !== null} variant="secondary">
              {busy === 'preview' ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
              <span className="ml-2">Preview</span>
            </Button>
            <Button onClick={onPdf} disabled={busy !== null}>
              <Download className="h-4 w-4" />
              <span className="ml-2">PDF</span>
            </Button>
          </ActionRow>
        </div>

        {!preview ? (
          <EmptyState
            icon={<FileBarChart className="h-6 w-6" />}
            title="Click Preview to load the financial summary"
            description="Default window is the last 12 months. Use From/To to narrow."
          />
        ) : (
          <div className="space-y-vt-3">
            <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-5">
              <Kpi label="Billed" value={INR(preview.totals.billed_inr)} />
              <Kpi label="Collected" value={INR(preview.totals.collected_inr)} tone="ok" />
              <Kpi label="Outstanding" value={INR(preview.totals.outstanding_inr)} tone="warn" />
              <Kpi label="GST" value={INR(preview.totals.gst_inr)} />
              <Kpi label="Late fees" value={INR(preview.totals.late_fees_inr)} />
            </div>
            <SimpleTable
              caption="Per-month breakdown"
              headers={['Month', 'Billed', 'Collected', 'Outstanding', 'GST', 'Late fees']}
              rows={preview.months.map((m) => [
                m.month,
                INR(m.billed),
                INR(m.collected),
                INR(m.outstanding),
                INR(m.gst),
                INR(m.late_fees)
              ])}
            />
            {preview.line_items_breakdown.length > 0 && (
              <SimpleTable
                caption="Line-item breakdown"
                headers={['Charge', 'Amount']}
                rows={preview.line_items_breakdown.map((r) => [r.label, INR(r.amount)])}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Defaulter aging tab ────────────────────────────────────────────────────

function DefaulterTab(): JSX.Element {
  const [asOf, setAsOf] = useState<string>(today());
  const [preview, setPreview] = useState<DefaulterPreview | null>(null);
  const [busy, setBusy] = useState<null | 'preview' | 'pdf' | 'excel'>(null);

  const params = useMemo(() => ({ as_of: isoEnd(asOf) }), [asOf]);

  const onPreview = async (): Promise<void> => {
    try {
      setBusy('preview');
      const data = await get<DefaulterPreview>('/reports/defaulter-aging', {
        params: { ...params, format: 'json' }
      });
      setPreview(data);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const onDownload = async (fmt: 'pdf' | 'excel'): Promise<void> => {
    try {
      setBusy(fmt);
      await downloadReport('/reports/defaulter-aging', { ...params, format: fmt });
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-vt-4 p-vt-4">
        <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-4">
          <Field label="As of">
            <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          </Field>
          <ActionRow className="md:col-span-3">
            <Button onClick={onPreview} disabled={busy !== null} variant="secondary">
              {busy === 'preview' ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
              <span className="ml-2">Preview</span>
            </Button>
            <Button onClick={() => onDownload('pdf')} disabled={busy !== null}>
              <Download className="h-4 w-4" />
              <span className="ml-2">PDF</span>
            </Button>
            <Button onClick={() => onDownload('excel')} disabled={busy !== null} variant="secondary">
              <Download className="h-4 w-4" />
              <span className="ml-2">Excel</span>
            </Button>
          </ActionRow>
        </div>

        {!preview ? (
          <EmptyState icon={<AlertCircle className="h-6 w-6" />} title="Click Preview to compute defaulter aging" />
        ) : (
          <div className="space-y-vt-3">
            <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-6">
              <Kpi label="Current" value={INR(preview.totals_by_bucket.current_inr)} />
              <Kpi label="0-30d" value={INR(preview.totals_by_bucket.d0_30_inr)} />
              <Kpi label="31-60d" value={INR(preview.totals_by_bucket.d31_60_inr)} tone="warn" />
              <Kpi label="61-90d" value={INR(preview.totals_by_bucket.d61_90_inr)} tone="warn" />
              <Kpi label="90+ d" value={INR(preview.totals_by_bucket.d90_plus_inr)} tone="alert" />
              <Kpi label="Total" value={INR(preview.totals_by_bucket.grand_total_inr)} />
            </div>
            <SimpleTable
              caption="Per-unit breakdown"
              headers={['Unit', 'Current', '0-30d', '31-60d', '61-90d', '90+ d', 'Total']}
              rows={preview.rows.map((r) => [
                r.unit_label,
                INR(r.current_inr),
                INR(r.d0_30_inr),
                INR(r.d31_60_inr),
                INR(r.d61_90_inr),
                INR(r.d90_plus_inr),
                INR(r.total_inr)
              ])}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Visitor tab ────────────────────────────────────────────────────────────

function VisitorTab(): JSX.Element {
  const [from, setFrom] = useState<string>(daysAgo(30));
  const [to, setTo] = useState<string>(today());
  const [preview, setPreview] = useState<VisitorPreview | null>(null);
  const [busy, setBusy] = useState<null | 'preview' | 'pdf'>(null);

  const params = useMemo(() => ({ from: isoStart(from), to: isoEnd(to) }), [from, to]);

  const onPreview = async (): Promise<void> => {
    try {
      setBusy('preview');
      const data = await get<VisitorPreview>('/reports/visitor-trend', {
        params: { ...params, format: 'json' }
      });
      setPreview(data);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const onPdf = async (): Promise<void> => {
    try {
      setBusy('pdf');
      await downloadReport('/reports/visitor-trend', { ...params, format: 'pdf' });
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-vt-4 p-vt-4">
        <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-4">
          <Field label="From">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <ActionRow className="md:col-span-2">
            <Button onClick={onPreview} disabled={busy !== null} variant="secondary">
              {busy === 'preview' ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
              <span className="ml-2">Preview</span>
            </Button>
            <Button onClick={onPdf} disabled={busy !== null}>
              <Download className="h-4 w-4" />
              <span className="ml-2">PDF</span>
            </Button>
          </ActionRow>
        </div>

        {!preview ? (
          <EmptyState icon={<FileBarChart className="h-6 w-6" />} title="Click Preview to load visitor trends" />
        ) : (
          <div className="space-y-vt-3">
            <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
              <Kpi label="Approved" value={preview.totals.approved} tone="ok" />
              <Kpi label="Denied" value={preview.totals.denied} tone="alert" />
              <Kpi label="FV check-ins" value={preview.totals.fv_checkins} />
              <Kpi label="Vehicle entries" value={preview.totals.vehicle_entries} />
            </div>
            <SimpleTable
              caption="Top 10 most-visited units"
              headers={['Unit', 'Visits']}
              rows={preview.top_units.map((u) => [u.label, String(u.count)])}
            />
            <SimpleTable
              caption="Top 10 frequent visitors"
              headers={['Name', 'Phone', 'Visits']}
              rows={preview.top_visitors.map((v) => [v.name, v.phone, String(v.count)])}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Helper attendance tab ──────────────────────────────────────────────────

function HelperTab(): JSX.Element {
  const [from, setFrom] = useState<string>(daysAgo(30));
  const [to, setTo] = useState<string>(today());
  const [unitId, setUnitId] = useState<string>('');
  const [preview, setPreview] = useState<HelperPreview | null>(null);
  const [busy, setBusy] = useState<null | 'preview' | 'pdf' | 'excel'>(null);

  const params = useMemo(() => {
    const p: Record<string, string> = { from: isoStart(from), to: isoEnd(to) };
    if (unitId.trim()) p.unit_id = unitId.trim();
    return p;
  }, [from, to, unitId]);

  const onPreview = async (): Promise<void> => {
    try {
      setBusy('preview');
      const data = await get<HelperPreview>('/reports/helper-attendance', {
        params: { ...params, format: 'json' }
      });
      setPreview(data);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const onDownload = async (fmt: 'pdf' | 'excel'): Promise<void> => {
    try {
      setBusy(fmt);
      await downloadReport('/reports/helper-attendance', { ...params, format: fmt });
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-vt-4 p-vt-4">
        <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-5">
          <Field label="From">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label="Unit ID (optional)">
            <Input placeholder="UUID" value={unitId} onChange={(e) => setUnitId(e.target.value)} />
          </Field>
          <ActionRow className="md:col-span-2">
            <Button onClick={onPreview} disabled={busy !== null} variant="secondary">
              {busy === 'preview' ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
              <span className="ml-2">Preview</span>
            </Button>
            <Button onClick={() => onDownload('pdf')} disabled={busy !== null}>
              <Download className="h-4 w-4" />
              <span className="ml-2">PDF</span>
            </Button>
            <Button onClick={() => onDownload('excel')} disabled={busy !== null} variant="secondary">
              <Download className="h-4 w-4" />
              <span className="ml-2">Excel</span>
            </Button>
          </ActionRow>
        </div>

        {!preview ? (
          <EmptyState icon={<FileBarChart className="h-6 w-6" />} title="Click Preview to load helper attendance" />
        ) : (
          <div className="space-y-vt-3">
            <SimpleTable
              caption="Per helper (FV)"
              headers={['Name', 'Kind', 'Phone', 'Days', 'Avg min', 'No-shows']}
              rows={preview.per_fv.map((r) => [
                r.name,
                r.kind,
                r.phone,
                String(r.attendance_days),
                r.avg_minutes == null ? '—' : String(r.avg_minutes),
                String(r.no_show_count)
              ])}
            />
            <SimpleTable
              caption="Per unit"
              headers={['Unit', 'Check-ins']}
              rows={preview.per_unit.map((r) => [r.unit_label, String(r.total_checkins)])}
            />
            {preview.police_verification_expiring.length > 0 && (
              <SimpleTable
                caption="Compliance: police verification expiring ≤ 60 days"
                headers={['Name', 'Kind', 'Verified at', 'Expires in (d)']}
                rows={preview.police_verification_expiring.map((r) => [
                  r.name,
                  r.kind,
                  r.police_verified_at.slice(0, 10),
                  String(r.expires_in_days)
                ])}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Election participation tab ─────────────────────────────────────────────

function ElectionTab(): JSX.Element {
  const [electionId, setElectionId] = useState<string>('');
  const [preview, setPreview] = useState<ElectionPreview | null>(null);
  const [busy, setBusy] = useState<null | 'preview' | 'pdf' | 'list'>(null);
  const [elections, setElections] = useState<ElectionListItem[] | null>(null);

  const loadElections = async (): Promise<void> => {
    try {
      setBusy('list');
      // Use the elections list endpoint to populate the dropdown. The actual
      // response shape may vary across deploys (some return `items`, some an
      // array). We accept both — fall back to empty list on shape mismatch.
      const data = await get<unknown>('/elections', { params: { limit: 50 } });
      const items = Array.isArray(data)
        ? (data as ElectionListItem[])
        : (data as { items?: ElectionListItem[] }).items ?? [];
      setElections(items);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const onPreview = async (): Promise<void> => {
    if (!electionId) {
      toast.error('Select an election first');
      return;
    }
    try {
      setBusy('preview');
      const data = await get<ElectionPreview>('/reports/election-participation', {
        params: { election_id: electionId, format: 'json' }
      });
      setPreview(data);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const onPdf = async (): Promise<void> => {
    if (!electionId) {
      toast.error('Select an election first');
      return;
    }
    try {
      setBusy('pdf');
      await downloadReport('/reports/election-participation', { election_id: electionId, format: 'pdf' });
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-vt-4 p-vt-4">
        <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-5">
          <Field label="Election">
            {elections === null ? (
              <Button onClick={loadElections} variant="secondary" disabled={busy === 'list'}>
                {busy === 'list' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Load elections'}
              </Button>
            ) : (
              <Select value={electionId} onChange={(e) => setElectionId(e.target.value)}>
                <option value="">— Select election —</option>
                {elections.map((el) => (
                  <option key={el.id} value={el.id}>
                    {el.title} ({el.status})
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Or paste election ID">
            <Input placeholder="UUID" value={electionId} onChange={(e) => setElectionId(e.target.value)} />
          </Field>
          <ActionRow className="md:col-span-3">
            <Button onClick={onPreview} disabled={busy !== null} variant="secondary">
              {busy === 'preview' ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
              <span className="ml-2">Preview</span>
            </Button>
            <Button onClick={onPdf} disabled={busy !== null}>
              <Download className="h-4 w-4" />
              <span className="ml-2">PDF</span>
            </Button>
          </ActionRow>
        </div>

        {!preview ? (
          <EmptyState icon={<FileBarChart className="h-6 w-6" />} title="Pick an election + click Preview" />
        ) : (
          <div className="space-y-vt-3">
            <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
              <Kpi label="Eligible voters" value={preview.turnout.eligible} />
              <Kpi label="Votes cast" value={preview.turnout.voted} />
              <Kpi label="Turnout %" value={`${preview.turnout.pct}%`} tone="ok" />
              <Kpi label="Ballot" value={preview.election.secret_ballot ? 'Secret' : 'Open'} />
            </div>
            <SimpleTable
              caption="Turnout by tower"
              headers={['Tower', 'Votes']}
              rows={preview.by_tower.map((r) => [r.tower, String(r.count)])}
            />
            <SimpleTable
              caption="Candidates + final results"
              headers={['Name', 'Unit', 'Votes', 'Rank', 'Winner?', 'Withdrawn?']}
              rows={preview.candidates.map((c) => [
                c.name,
                c.unit,
                c.vote_count == null ? '—' : String(c.vote_count),
                c.position == null ? '—' : String(c.position),
                c.declared_winner ? 'Yes' : '',
                c.withdrawn ? 'Yes' : ''
              ])}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Audit log tab ──────────────────────────────────────────────────────────

function AuditTab(): JSX.Element {
  const [from, setFrom] = useState<string>(daysAgo(30));
  const [to, setTo] = useState<string>(today());
  const [action, setAction] = useState<string>('');
  const [actorRole, setActorRole] = useState<string>('');
  const [entityType, setEntityType] = useState<string>('');
  const [preview, setPreview] = useState<AuditPreview | null>(null);
  const [busy, setBusy] = useState<null | 'preview' | 'csv'>(null);

  const params = useMemo(() => {
    const p: Record<string, string> = { from: isoStart(from), to: isoEnd(to) };
    if (action.trim()) p.action = action.trim();
    if (actorRole.trim()) p.actor_role = actorRole.trim();
    if (entityType.trim()) p.entity_type = entityType.trim();
    return p;
  }, [from, to, action, actorRole, entityType]);

  const onPreview = async (): Promise<void> => {
    try {
      setBusy('preview');
      const data = await get<AuditPreview>('/reports/audit-log', {
        params: { ...params, format: 'json' }
      });
      setPreview(data);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const onCsv = async (): Promise<void> => {
    try {
      setBusy('csv');
      await downloadReport('/reports/audit-log', { ...params, format: 'csv' });
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-vt-4 p-vt-4">
        <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-6">
          <Field label="From">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label="Action (or prefix.*)">
            <Input placeholder="parking.* or bill.create" value={action} onChange={(e) => setAction(e.target.value)} />
          </Field>
          <Field label="Actor role">
            <Input placeholder="admin / accountant / …" value={actorRole} onChange={(e) => setActorRole(e.target.value)} />
          </Field>
          <Field label="Entity type">
            <Input placeholder="bill / vehicle / …" value={entityType} onChange={(e) => setEntityType(e.target.value)} />
          </Field>
          <ActionRow>
            <Button onClick={onPreview} disabled={busy !== null} variant="secondary">
              {busy === 'preview' ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
              <span className="ml-2">Preview</span>
            </Button>
            <Button onClick={onCsv} disabled={busy !== null}>
              <Download className="h-4 w-4" />
              <span className="ml-2">CSV</span>
            </Button>
          </ActionRow>
        </div>

        {!preview ? (
          <EmptyState
            icon={<FileBarChart className="h-6 w-6" />}
            title="Click Preview to inspect the matched audit-log rows"
            description="CSV export streams unlimited rows (paged 500 at a time)."
          />
        ) : (
          <SimpleTable
            caption={`First ${preview.items.length} matching rows (capped at ${preview.capped_at} for preview)`}
            headers={['Timestamp', 'Action', 'Actor role', 'Entity']}
            rows={preview.items.map((r) => [
              new Date(r.at).toISOString().replace('T', ' ').slice(0, 19),
              r.action,
              r.actor_role,
              `${r.entity_type}${r.entity_id ? ` ${r.entity_id.slice(0, 8)}` : ''}`
            ])}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ─── Small reusables (local — no extra UI primitive added) ──────────────────

interface FieldProps {
  label: string;
  children: React.ReactNode;
}
function Field({ label, children }: FieldProps): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-vt-11 text-vt-text-muted">{label}</span>
      {children}
    </div>
  );
}

interface ActionRowProps {
  children: React.ReactNode;
  className?: string;
}
function ActionRow({ children, className }: ActionRowProps): JSX.Element {
  return <div className={`flex items-end gap-vt-2 ${className ?? ''}`}>{children}</div>;
}

interface KpiProps {
  label: string;
  value: string | number;
  tone?: 'ok' | 'warn' | 'alert';
}
function Kpi({ label, value, tone }: KpiProps): JSX.Element {
  const toneCls =
    tone === 'ok'
      ? 'text-[color:var(--vt-safe)]'
      : tone === 'warn'
      ? 'text-vt-warn'
      : tone === 'alert'
      ? 'text-vt-alert'
      : 'text-vt-text';
  return (
    <div className="rounded-vt-md border border-vt-border bg-vt-surface p-vt-3">
      <div className="text-vt-12 text-vt-text-muted">{label}</div>
      <div className={`text-vt-18 font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}

interface SimpleTableProps {
  caption: string;
  headers: string[];
  rows: ReadonlyArray<ReadonlyArray<string>>;
}
function SimpleTable({ caption, headers, rows }: SimpleTableProps): JSX.Element {
  if (rows.length === 0) {
    return (
      <div className="rounded-vt-md border border-vt-border bg-vt-surface p-vt-4 text-vt-13 text-vt-text-muted">
        <div className="mb-vt-1 font-medium text-vt-text">{caption}</div>
        No rows in this window.
      </div>
    );
  }
  return (
    <div className="space-y-vt-1">
      <div className="text-vt-13 font-medium text-vt-text">{caption}</div>
      <div className="overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface">
        <Table>
          <TableHeader>
            <TableRow>
              {headers.map((h) => (
                <TableHead key={h}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                {r.map((cell, j) => (
                  <TableCell key={j}>{cell}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// Loading placeholder when needed
export function ReportsLoading(): JSX.Element {
  return (
    <div className="p-vt-4">
      <Skeleton className="h-8 w-48" />
    </div>
  );
}
